import { COLORS } from '../design/tokens';
import { createRandom, hashString, toDataUri } from './seed';

const WIDTH = 1600;
const HEIGHT = 1400;

export interface BackdropOptions {
  readonly seed: string;
  /**
   * When true, emits no `<style>` block at all.
   *
   * CSS animation inside an SVG-as-image DOES run, but whether
   * `prefers-reduced-motion` is honoured inside that isolated document varies by
   * engine. § UI/UX requires perpetual motion to be REMOVED under reduced motion, not
   * shortened, so the caller decides and the generator emits two different documents.
   * That is why this is a parameter rather than a media query in the SVG.
   */
  readonly reducedMotion: boolean;
}

/** Layered ridgelines, a horizon halo and a constellation field. */
export function generateBackdrop({ seed, reducedMotion }: BackdropOptions): string {
  const random = createRandom(hashString(seed));
  const parts: string[] = [];

  const animations = reducedMotion
    ? ''
    : '<style>' +
      '@keyframes tw{0%,100%{opacity:.12}50%{opacity:.7}}' +
      '@keyframes glow{0%,100%{opacity:.7}50%{opacity:1}}' +
      '.st{animation:tw 5s ease-in-out infinite}' +
      '.halo{animation:glow 9s ease-in-out infinite}' +
      '</style>';

  parts.push(
    '<defs>' +
      '<linearGradient id="sky" x1="0" y1="0" x2="0.3" y2="1">' +
      '<stop offset="0%" stop-color="#17141A"/>' +
      '<stop offset="55%" stop-color="#0E0E11"/>' +
      '<stop offset="100%" stop-color="#08080A"/></linearGradient>' +
      '<radialGradient id="sun" cx="0.5" cy="0.5" r="0.5">' +
      `<stop offset="0%" stop-color="${COLORS.accent}" stop-opacity="0.5"/>` +
      `<stop offset="55%" stop-color="${COLORS.accentDeep}" stop-opacity="0.15"/>` +
      `<stop offset="100%" stop-color="${COLORS.accentDeep}" stop-opacity="0"/></radialGradient>` +
      '<linearGradient id="ridge" x1="0" y1="0" x2="0" y2="1">' +
      `<stop offset="0%" stop-color="${COLORS.accent}" stop-opacity="0.22"/>` +
      `<stop offset="100%" stop-color="${COLORS.accent}" stop-opacity="0"/></linearGradient>` +
      animations +
      '</defs>',
  );

  parts.push(`<rect width="${WIDTH}" height="${HEIGHT}" fill="url(#sky)"/>`);

  const haloClass = reducedMotion ? '' : ' class="halo"';
  parts.push(
    `<ellipse${haloClass} cx="${WIDTH * 0.4}" cy="${HEIGHT * 0.34}" ` +
      `rx="${WIDTH * 0.45}" ry="${HEIGHT * 0.3}" fill="url(#sun)"/>`,
  );

  // Constellation field.
  const starClass = reducedMotion ? '' : ' class="st"';
  for (let index = 0; index < 110; index += 1) {
    const cx = (random() * WIDTH).toFixed(0);
    const cy = (random() * HEIGHT * 0.6).toFixed(0);
    const r = (0.8 + random() * 2).toFixed(1);
    const delay = reducedMotion
      ? ''
      : ` style="animation-delay:${(random() * 5).toFixed(1)}s"`;

    parts.push(
      `<circle${starClass} cx="${cx}" cy="${cy}" r="${r}" fill="${COLORS.text}" opacity="0.3"${delay}/>`,
    );
  }

  // Ridgelines, back to front.
  const ridgeFills = ['#0B0B0D', '#0D0D10', '#101014', '#131318', '#17171C'];
  for (let layer = 0; layer < 5; layer += 1) {
    const baseY = HEIGHT * (0.5 + layer * 0.11);
    const amplitude = 110 - layer * 14;
    const steps = 8;

    let path = `M 0 ${HEIGHT} L 0 ${baseY.toFixed(0)}`;

    for (let step = 1; step <= steps; step += 1) {
      const px = (WIDTH / steps) * step;
      const py =
        baseY -
        Math.sin(step * (1.1 + layer * 0.3) + layer) * amplitude -
        random() * amplitude * 0.4;

      path +=
        ` Q ${(px - WIDTH / steps / 2).toFixed(0)} ${(py - amplitude * 0.4).toFixed(0)}` +
        ` ${px.toFixed(0)} ${py.toFixed(0)}`;
    }

    path += ` L ${WIDTH} ${HEIGHT} Z`;

    parts.push(
      `<path d="${path}" fill="${ridgeFills[layer]}" opacity="${(0.92 - layer * 0.13).toFixed(2)}"/>`,
    );
    parts.push(
      `<path d="${path}" fill="url(#ridge)" opacity="${(0.3 - layer * 0.05).toFixed(2)}"/>`,
    );
  }

  return toDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${WIDTH} ${HEIGHT}" ` +
      `width="${WIDTH}" height="${HEIGHT}" role="presentation">${parts.join('')}</svg>`,
  );
}
